<template>
  <v-card>
    <v-card-title>Platforms</v-card-title>

    <v-data-table :headers="headers" :items="processedPlatforms">
      <template v-slot:[`item.name`]="{ item }">
        <v-menu
          v-model="item.showForestPlatformSelector"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <span v-on="on" style="cursor: pointer">
              <span v-if="item.name && item.name.length > 20">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ truncateText(item.name) }}</span>
                  </template>
                  <span>{{ item.name }}</span>
                </v-tooltip>
              </span>
              <span v-else>
                {{ item.name }}
              </span>
            </span>
          </template>
          <v-list dense>
            <v-list-item
              @click="selectOption(item, 'Forest Platform ID', item.id)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest Platform ID
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="selectOption(item, 'Forest Platform Name', item.name)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest Platform Name
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.type`]="{ item }">
        <v-menu
          v-model="item.showPlatformSelector"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" style="cursor: pointer">
              <platform-logo :platform="item.type"></platform-logo>
            </div>
          </template>
          <v-list dense>
            <v-list-item style="cursor: pointer">
              <v-list-item-title class="font-weight-medium">
                <a
                  :href="getPlatformDetail(item.platform).url"
                  target="_blank"
                  style="text-decoration: none; align-items: center"
                >
                  Go to Platform Page 🔗
                </a>
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="selectOption(item, 'Platform ID', item.platform.id)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Platform ID
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="selectOption(item, 'Platform Name', item.platform.name)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Platform Name
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="
                copyForestPlatformUrl(
                  item,
                  'URL',
                  getPlatformDetail(item.platform).url
                )
              "
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Platform URL
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="
                copyForestPlatformUniqueId(
                  item,
                  'Unique ID',
                  item.platform.uniqueId
                )
              "
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Platform Unique ID
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.platform`]="{ item }">
        {{ item.platform.uniqueId }}
      </template>
      <template v-slot:[`item.plantingReviewOption`]="{ item }">
        <template
          v-if="
            item.plantingReviewOption ===
            $options.TREE_PLANTING_OPTION.ONLY_NEW_REVIEWS
          "
        >
          Only new reviews
        </template>
        <template v-else>All reviews</template>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <template
          v-if="item.type === $options.PLATFORMS.GOOGLE && !item.keys.gmb"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                class="ma-2"
                text
                icon
                color="primary"
                @click="openEditPlatformModal(item)"
              >
                <v-icon>edit</v-icon>
              </v-btn>
            </template>
            <span>Edit Platform</span>
          </v-tooltip>
          <forest-platform-edit :platform="item" ref="platformEditModal">
          </forest-platform-edit>
        </template>
      </template>

      <template v-slot:[`item.lastReportDate`]="{ item }">
        {{ formatDate(item.lastReportDate) }}
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>
    </v-data-table>

    <!-- Success message snackbar -->
    <success-snackbar v-model="showSuccessMessage" :message="successMessage" />
  </v-card>
</template>
<script>
import {
  PLATFORMS,
  PLATFORM_TYPES,
  TREE_PLANTING_OPTION,
} from "../../constants";
import PlatformLogo from "../Base/PlatformLogo.vue";
import { truncateText, formatDate } from "../../helpers";
import ForestPlatformEdit from "./ForestPlatformEdit.vue";
import { writeToClipboard } from "../../utils/copyText";
import SuccessSnackbar from "../Base/SuccessSnackbar.vue";

export default {
  name: "ForestPlatformsTable",
  PLATFORMS,
  PLATFORM_TYPES,
  TREE_PLANTING_OPTION,
  components: {
    PlatformLogo,
    ForestPlatformEdit,
    SuccessSnackbar,
  },
  props: {
    platforms: {
      type: Array,
      required: true,
    },
  },
  computed: {
    processedPlatforms() {
      return this.platforms.map((platform) => ({
        ...platform,
        showPlatformSelector: false,
        showForestPlatformSelector: false,
      }));
    },
  },
  data() {
    return {
      truncateText,
      formatDate,
      copiedKey: null, // 'platform' | 'forest' | null
      resetTimer: undefined,
      showSuccessMessage: false,
      successMessage: "",
      headers: [
        {
          text: "Name",
          sortable: true,
          value: "name",
        },
        {
          text: "Platform",
          sortable: true,
          value: "type",
        },
        {
          text: "Review amount",
          sortable: true,
          value: "reviewAmount",
        },
        {
          text: "Review amount on platform",
          sortable: true,
          value: "reviewAmountOnPlatform",
        },
        {
          text: "Rating",
          sortable: true,
          value: "score",
        },
        {
          text: "Planted trees",
          sortable: true,
          value: "reviewTreeAmount",
        },
        {
          text: "Plant trees for",
          sortable: true,
          value: "plantingReviewOption",
        },
        {
          text: "Last Report",
          sortable: true,
          value: "lastReportDate",
        },
        {
          text: "Created At",
          sortable: true,
          value: "createdAt",
        },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  methods: {
    getPlatformDetail(platform) {
      console.log(platform);
      let detail = "";
      let url = "";
      switch (platform.type) {
        case PLATFORMS.AMAZON:
          url = `https://www.${platform[platform.type].domain}/dp/${
            platform[platform.type].asin
          }`;
          detail = platform.name;
          break;
        case PLATFORMS.FACEBOOK:
          url = `https://www.facebook.com/${platform.uniqueId}`;
          detail = platform.name;
          break;
        case PLATFORMS.GOOGLE:
          url = platform[platform.type]?.url;
          detail = `${platform[platform.type]?.address}, ${url}`;
          break;
        case PLATFORMS.TRUSTPILOT:
          detail = platform[platform.type]?.businessUnit?.identifyingName;
          url = `https://trustpilot.com/review/${platform.uniqueId}`;
          break;
        case PLATFORMS.KUNUNU:
          detail = platform[platform.type].simpleName;
          url = `https://kununu.com/${platform[platform.type].countryCode}/${
            platform[platform.type].slug
          }`;
          break;
        case PLATFORMS.GLASSDOOR:
          detail = platform[platform.type]?.employer?.shortName;
          url = platform[platform.type]?.companyUrl;
          break;
        case PLATFORMS.G2:
          detail = platform.name;
          url = `https://www.g2.com/products/${platform.uniqueId}`;
          break;
        case PLATFORMS.GARTNER:
          detail = platform.name;
          url = platform.url;
          break;
        case PLATFORMS.PROVEN_EXPERT:
          detail = platform.name;
          url = platform.url;
          break;
        case PLATFORMS.APPLE_APPSTORE:
          detail = platform.name;
          url = platform.url;
          break;
        case PLATFORMS.GOOGLE_PLAYSTORE:
          detail = platform.name;
          url = platform.url;
          break;
        case PLATFORMS.TRUSTEDSHOPS:
          detail = platform.name;
          url = platform.url;
          break;
        default:
          break;
      }

      return {
        url,
        detail,
      };
    },
    openEditPlatformModal() {
      this.$refs.platformEditModal.dialogForestPlatformEdit = true;
    },
    async copyToClipboard(label, value, key) {
      try {
        await writeToClipboard(String(value));
        this.copiedKey = key;
        // Show success message
        this.successMessage = `${label} successfully copied to clipboard!`;
        this.showSuccessMessage = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to copy " + label, e);
      } finally {
        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => (this.copiedKey = null), 1500);
      }
    },
    selectOption(item, type, value) {
      item.showPlatformSelector = false;
      this.copyToClipboard(type, value, type);
    },
    copyForestPlatformUrl(item, type, value) {
      item.showForestPlatformSelector = false;
      this.copyToClipboard(type, value, type);
    },
    copyForestPlatformUniqueId(item, type, value) {
      item.showForestPlatformSelector = false;
      this.copyToClipboard(type, value, type);
    },
  },
};
</script>
